export var ServiceContainerKey;
(function (ServiceContainerKey) {
    ServiceContainerKey["Config"] = "config";
    ServiceContainerKey["Broadcast"] = "broadcast";
    ServiceContainerKey["Logger"] = "logger";
    ServiceContainerKey["LogManager"] = "logManager";
    ServiceContainerKey["CacheManager"] = "cacheManager";
    ServiceContainerKey["Auth"] = "auth";
    ServiceContainerKey["Device"] = "device";
    ServiceContainerKey["Player"] = "player";
    ServiceContainerKey["MediaControl"] = "mediaControl";
    ServiceContainerKey["Axios"] = "axios";
    ServiceContainerKey["Api"] = "api";
    ServiceContainerKey["SignageApplication"] = "signageApplication";
    ServiceContainerKey["Screen"] = "screen";
    ServiceContainerKey["MultiInstanceManager"] = "multiInstanceManager";
    ServiceContainerKey["ProcessAction"] = "processAction";
    ServiceContainerKey["Lifecycle"] = "lifecycle";
    ServiceContainerKey["ScreenVendor"] = "screenVendor";
    ServiceContainerKey["HealthcheckManager"] = "healthcheckManager";
    ServiceContainerKey["WindowPlacer"] = "windowPlacer";
})(ServiceContainerKey || (ServiceContainerKey = {}));
export class AbstractServiceContainer {
    constructor() {
        this.factories = new Map();
        this.instances = new Map();
    }
    /**
     * Register a service factory (will be created as singleton on first resolve)
     */
    register(name, factory) {
        this.factories.set(name, factory);
    }
    /**
     * Register an existing instance directly
     */
    instance(name, instance) {
        this.instances.set(name, instance);
    }
    /**
     * Resolve a service by name (creates singleton instance on first call)
     */
    resolve(name) {
        // Return cached instance if exists
        if (this.instances.has(name)) {
            return this.instances.get(name);
        }
        // Create new instance from factory and cache it
        if (this.factories.has(name)) {
            const factory = this.factories.get(name);
            if (!factory) {
                throw new Error(`Service '${name}' not found in container`);
            }
            const instance = factory();
            this.instances.set(name, instance);
            return instance;
        }
        throw new Error(`Service '${name}' not found in container`);
    }
    /**
     * Check if a service is registered
     */
    has(name) {
        return this.factories.has(name) || this.instances.has(name);
    }
    /**
     * Get all registered service names
     */
    getServiceNames() {
        const names = new Set();
        this.factories.forEach((_, name) => names.add(name));
        this.instances.forEach((_, name) => names.add(name));
        return Array.from(names);
    }
}
/**
 * App facade for easy service access
 */
export class AbstractApp {
    static config() {
        return this.serviceContainer.resolve(ServiceContainerKey.Config);
    }
    static broadcast() {
        return this.serviceContainer.resolve(ServiceContainerKey.Broadcast);
    }
    static logger() {
        return this.serviceContainer.resolve(ServiceContainerKey.Logger);
    }
    static logManager() {
        return this.serviceContainer.resolve(ServiceContainerKey.LogManager);
    }
    static cacheManager() {
        return this.serviceContainer.resolve(ServiceContainerKey.CacheManager);
    }
    static lifecycle() {
        return this.serviceContainer.resolve(ServiceContainerKey.Lifecycle);
    }
    static auth() {
        return this.serviceContainer.resolve(ServiceContainerKey.Auth);
    }
    static device() {
        return this.serviceContainer.resolve(ServiceContainerKey.Device);
    }
    static player() {
        return this.serviceContainer.resolve(ServiceContainerKey.Player);
    }
    static mediaControl() {
        return this.serviceContainer.resolve(ServiceContainerKey.MediaControl);
    }
    static screen() {
        return this.serviceContainer.resolve(ServiceContainerKey.Screen);
    }
    static screenVendor() {
        return this.serviceContainer.resolve(ServiceContainerKey.ScreenVendor);
    }
    static healthcheckManager() {
        return this.serviceContainer.resolve(ServiceContainerKey.HealthcheckManager);
    }
    static multiInstance() {
        if (!this.serviceContainer.has(ServiceContainerKey.MultiInstanceManager)) {
            return null;
        }
        return this.serviceContainer.resolve(ServiceContainerKey.MultiInstanceManager);
    }
    /**
     * Must be initialized from main.ts after AuthService is ready
     */
    static axios() {
        return this.serviceContainer.resolve(ServiceContainerKey.Axios);
    }
    static api() {
        return this.serviceContainer.resolve(ServiceContainerKey.Api);
    }
    static processAction() {
        return this.serviceContainer.resolve(ServiceContainerKey.ProcessAction);
    }
    static signageApplication() {
        return this.serviceContainer.resolve(ServiceContainerKey.SignageApplication);
    }
    static container() {
        return this.serviceContainer;
    }
}
//# sourceMappingURL=AbstractServiceContainer.js.map